import { useEffect, useRef, useState, useMemo } from "react";
import {
  parseISO,
  isSameDay,
  isToday,
  format,
  addDays,
  subDays,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Video,
  Check,
  Edit2,
  Trash2,
  Plus,
  Calendar as CalendarIcon,
  GripVertical,
} from "lucide-react";
import { useDataProvider, useNotify } from "ra-core";
import type { Task } from "../types";
import { getActivityTypeConfig } from "./activityTypes";
import {
  syncTaskWithGoogleCalendar,
  deleteEventFromGoogleCalendar,
} from "../providers/google/googleIntegrationService";
import { cn } from "@/lib/utils";

interface AgendaDayViewProps {
  currentDate: Date;
  tasks: Task[];
  onEditTask: (task: Task) => void;
  onSelectSlot: (date: Date) => void;
  onRefresh: () => void;
  onSelectDate?: (date: Date) => void;
}

// Configuração do Grid de Horários
const HOUR_HEIGHT = 80; // 80px por hora
const MINUTE_HEIGHT = HOUR_HEIGHT / 60; // ~1.333px por minuto
const SLOT_15_HEIGHT = HOUR_HEIGHT / 4; // 20px por bloco de 15 minutos
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const QUARTERS = [0, 15, 30, 45];

export const AgendaDayView = ({
  currentDate,
  tasks,
  onEditTask,
  onSelectSlot,
  onRefresh,
  onSelectDate,
}: AgendaDayViewProps) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const isJustResizedRef = useRef(false);
  const [resizingTaskId, setResizingTaskId] = useState<string | number | null>(
    null,
  );
  const [tempDurationMap, setTempDurationMap] = useState<
    Record<string | number, number>
  >({});
  const [draggedTaskId, setDraggedTaskId] = useState<string | number | null>(
    null,
  );
  const [dragOverSlotKey, setDragOverSlotKey] = useState<string | null>(null);
  const [dragOverDayStrip, setDragOverDayStrip] = useState<string | null>(null);

  // Filtrar tarefas do dia atual
  const dayTasks = useMemo(() => {
    return tasks.filter((task) => {
      const taskDateStr = task.data_inicio || task.due_date;
      if (!taskDateStr) return false;
      try {
        return isSameDay(parseISO(taskDateStr), currentDate);
      } catch {
        return false;
      }
    });
  }, [tasks, currentDate]);

  // Tarefas sem horário definido (dia inteiro / sem hora)
  const untimedTasks = useMemo(() => {
    return dayTasks.filter((task) => {
      const taskDateStr = task.data_inicio || task.due_date;
      if (!taskDateStr) return true;
      try {
        parseISO(taskDateStr);
        return false;
      } catch {
        return true;
      }
    });
  }, [dayTasks]);

  // Tarefas com horário definido mapeadas para a timeline
  const timedEvents = useMemo(() => {
    const valid = dayTasks.filter((task) => {
      const str = task.data_inicio || task.due_date;
      if (!str) return false;
      try {
        parseISO(str);
        return true;
      } catch {
        return false;
      }
    });

    const parsed = valid.map((task) => {
      const start = parseISO(task.data_inicio || task.due_date!);
      const startMinutes = start.getHours() * 60 + start.getMinutes();

      let endMinutes = startMinutes + 60; // padrão 1 hora
      if (task.data_fim) {
        try {
          const end = parseISO(task.data_fim);
          const rawEndMinutes = end.getHours() * 60 + end.getMinutes();
          if (rawEndMinutes > startMinutes) {
            endMinutes = rawEndMinutes;
          }
        } catch {}
      }

      // Se estiver sendo redimensionado no momento, usa o valor temporário
      const activeDuration =
        tempDurationMap[task.id] !== undefined
          ? tempDurationMap[task.id]
          : Math.max(15, endMinutes - startMinutes);

      const computedEndMinutes = startMinutes + activeDuration;
      const top = startMinutes * MINUTE_HEIGHT;
      const height = Math.max(20, activeDuration * MINUTE_HEIGHT);

      return {
        task,
        startMinutes,
        endMinutes: computedEndMinutes,
        durationMinutes: activeDuration,
        top,
        height,
        column: 0,
        totalColumns: 1,
      };
    });

    // Algoritmo de overlap (atribuição de colunas paralelas se colidirem no tempo)
    parsed.sort(
      (a, b) =>
        a.startMinutes - b.startMinutes ||
        b.durationMinutes - a.durationMinutes,
    );

    for (let i = 0; i < parsed.length; i++) {
      const current = parsed[i];
      const overlapping = [current];

      for (let j = 0; j < parsed.length; j++) {
        if (i === j) continue;
        const other = parsed[j];
        // Verifica se há colisão de horário
        if (
          current.startMinutes < other.endMinutes &&
          current.endMinutes > other.startMinutes
        ) {
          overlapping.push(other);
        }
      }

      if (overlapping.length > 1) {
        overlapping.sort((a, b) => a.startMinutes - b.startMinutes);
        const total = overlapping.length;
        overlapping.forEach((ev, colIdx) => {
          ev.column = colIdx;
          ev.totalColumns = total;
        });
      }
    }

    return parsed;
  }, [dayTasks, tempDurationMap]);

  // Auto-scroll inicial para a hora atual ou 8h
  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const targetHour = isToday(currentDate)
      ? Math.max(7, new Date().getHours() - 1)
      : 8;
    const targetScrollTop = targetHour * HOUR_HEIGHT;
    scrollContainerRef.current.scrollTo({
      top: targetScrollTop,
      behavior: "smooth",
    });
  }, [currentDate]);

  // Redimensionamento por arrasto na barra inferior
  const handleResizeStart = (
    e: React.MouseEvent,
    task: Task,
    initialDuration: number,
    _startMinutes: number,
  ) => {
    e.stopPropagation();
    e.preventDefault();

    const startY = e.clientY;
    setResizingTaskId(task.id);
    setTempDurationMap((prev) => ({ ...prev, [task.id]: initialDuration }));

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startY;
      // Cada 20px de arrasto vertical = 15 minutos
      const step15 = Math.round(deltaY / SLOT_15_HEIGHT) * 15;
      const newDuration = Math.max(15, initialDuration + step15);
      setTempDurationMap((prev) => ({ ...prev, [task.id]: newDuration }));
    };

    const handleMouseUp = async (upEvent: MouseEvent) => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      setResizingTaskId(null);

      // Trava para evitar que o clique subsequente abra o painel/modal da atividade
      isJustResizedRef.current = true;
      const captureClickOnce = (captureEvent: MouseEvent) => {
        captureEvent.stopPropagation();
        captureEvent.preventDefault();
        window.removeEventListener("click", captureClickOnce, true);
      };
      window.addEventListener("click", captureClickOnce, true);

      setTimeout(() => {
        isJustResizedRef.current = false;
        window.removeEventListener("click", captureClickOnce, true);
      }, 400);

      const deltaY = upEvent.clientY - startY;
      const step15 = Math.round(deltaY / SLOT_15_HEIGHT) * 15;
      const finalDuration = Math.max(15, initialDuration + step15);

      if (finalDuration !== initialDuration) {
        const startStr =
          task.data_inicio || task.due_date || new Date().toISOString();
        const start = parseISO(startStr);
        const newEnd = new Date(start.getTime() + finalDuration * 60 * 1000);

        try {
          await dataProvider.update("tasks", {
            id: task.id,
            data: {
              ...task,
              data_fim: newEnd.toISOString(),
            },
            previousData: task,
          });

          // Sincroniza novo horário de término com o Google Agenda
          syncTaskWithGoogleCalendar({
            id: task.id,
            text: task.text,
            type: task.type,
            data_inicio: startStr,
            data_fim: newEnd.toISOString(),
            link_chamada: task.link_chamada,
          }).catch(() => {});

          notify(
            `Atividade ajustada: ${format(start, "HH:mm")} até ${format(newEnd, "HH:mm")}`,
            { type: "success" },
          );
          onRefresh();
        } catch {
          notify("Erro ao atualizar horário da atividade", { type: "error" });
        }
      }

      setTempDurationMap((prev) => {
        const copy = { ...prev };
        delete copy[task.id];
        return copy;
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const toggleTaskDone = async (e: React.MouseEvent, task: Task) => {
    e.stopPropagation();
    const isDone = !!task.done_date;
    try {
      await dataProvider.update("tasks", {
        id: task.id,
        data: {
          ...task,
          done_date: isDone ? null : new Date().toISOString(),
        },
        previousData: task,
      });
      notify(isDone ? "Atividade reaberta" : "Atividade concluída", {
        type: "success",
      });
      onRefresh();
    } catch {
      notify("Erro ao atualizar status", { type: "error" });
    }
  };

  const deleteTask = async (e: React.MouseEvent, task: Task) => {
    e.stopPropagation();
    if (!window.confirm("Deseja realmente excluir esta atividade?")) return;
    try {
      await dataProvider.delete("tasks", { id: task.id, previousData: task });
      // Remove do Google Agenda
      deleteEventFromGoogleCalendar(task.id).catch(() => {});
      notify("Atividade excluída", { type: "success" });
      onRefresh();
    } catch {
      notify("Erro ao excluir", { type: "error" });
    }
  };

  // Mini faixa de 7 dias ao redor do dia atual
  const dayStrip = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) =>
      addDays(subDays(currentDate, 3), i),
    );
  }, [currentDate]);

  // Soltar em um slot de 15 minutos na grade de 24h
  const handleDropTaskOnSlot = async (slotDate: Date, e: React.DragEvent) => {
    e.preventDefault();
    setDragOverSlotKey(null);
    setDraggedTaskId(null);

    const rawData = e.dataTransfer.getData("application/json");
    let taskId: string | number | null = null;
    let durationMinutes = 60;

    if (rawData) {
      try {
        const parsed = JSON.parse(rawData);
        taskId = parsed.taskId;
        if (parsed.durationMinutes) durationMinutes = parsed.durationMinutes;
      } catch {}
    }
    if (!taskId) {
      taskId = e.dataTransfer.getData("text/plain");
    }
    if (!taskId) return;

    const task = tasks.find((t) => String(t.id) === String(taskId));
    if (!task) return;

    if (task.data_inicio && task.data_fim) {
      try {
        const origStart = parseISO(task.data_inicio);
        const origEnd = parseISO(task.data_fim);
        const diff = Math.round(
          (origEnd.getTime() - origStart.getTime()) / 60000,
        );
        if (diff > 0) durationMinutes = diff;
      } catch {}
    }

    const newStart = new Date(slotDate);
    const newEnd = new Date(newStart.getTime() + durationMinutes * 60 * 1000);

    try {
      await dataProvider.update("tasks", {
        id: task.id,
        data: {
          ...task,
          data_inicio: newStart.toISOString(),
          due_date: newStart.toISOString(),
          data_fim: newEnd.toISOString(),
        },
        previousData: task,
      });
      notify(
        `Atividade movida para ${format(newStart, "HH:mm")} às ${format(newEnd, "HH:mm")}`,
        { type: "success" },
      );
      onRefresh();
    } catch {
      notify("Erro ao reagendar atividade", { type: "error" });
    }
  };

  // Soltar em outro dia através da mini faixa de dias
  const handleDropOnDay = async (targetDay: Date, e: React.DragEvent) => {
    e.preventDefault();
    setDragOverDayStrip(null);
    setDraggedTaskId(null);

    const rawData = e.dataTransfer.getData("application/json");
    let taskId: string | number | null = null;
    let durationMinutes = 60;

    if (rawData) {
      try {
        const parsed = JSON.parse(rawData);
        taskId = parsed.taskId;
        if (parsed.durationMinutes) durationMinutes = parsed.durationMinutes;
      } catch {}
    }
    if (!taskId) {
      taskId = e.dataTransfer.getData("text/plain");
    }
    if (!taskId) return;

    const task = tasks.find((t) => String(t.id) === String(taskId));
    if (!task) return;

    const newStart = new Date(targetDay);
    if (task.data_inicio || task.due_date) {
      try {
        const orig = parseISO(task.data_inicio || task.due_date!);
        newStart.setHours(orig.getHours(), orig.getMinutes(), 0, 0);
      } catch {
        newStart.setHours(9, 0, 0, 0);
      }
    } else {
      newStart.setHours(9, 0, 0, 0);
    }

    if (task.data_inicio && task.data_fim) {
      try {
        const origStart = parseISO(task.data_inicio);
        const origEnd = parseISO(task.data_fim);
        const diff = Math.round(
          (origEnd.getTime() - origStart.getTime()) / 60000,
        );
        if (diff > 0) durationMinutes = diff;
      } catch {}
    }

    const newEnd = new Date(newStart.getTime() + durationMinutes * 60 * 1000);

    try {
      await dataProvider.update("tasks", {
        id: task.id,
        data: {
          ...task,
          data_inicio: newStart.toISOString(),
          due_date: newStart.toISOString(),
          data_fim: newEnd.toISOString(),
        },
        previousData: task,
      });
      notify(
        `Atividade movida para ${format(newStart, "EEEE, d 'de' MMMM", { locale: ptBR })} às ${format(newStart, "HH:mm")}`,
        { type: "success" },
      );
      onRefresh();
      onSelectDate?.(targetDay);
    } catch {
      notify("Erro ao mover atividade para outro dia", { type: "error" });
    }
  };

  // Linha indicadora de tempo real "Agora"
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const currentLineTop = currentMinutes * MINUTE_HEIGHT;

  return (
    <div className="flex flex-col gap-3">
      {/* Tarefas sem horário definido */}
      {untimedTasks.length > 0 && (
        <div className="bg-muted/40 rounded-2xl p-3 border border-border/80 shadow-xs">
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <CalendarIcon className="w-3.5 h-3.5 text-primary" />
            Tarefas de dia inteiro / sem horário ({untimedTasks.length})
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {untimedTasks.map((t) => (
              <div
                key={t.id}
                draggable={true}
                onDragStart={(e) => {
                  setDraggedTaskId(t.id);
                  e.dataTransfer.setData(
                    "application/json",
                    JSON.stringify({ taskId: t.id, durationMinutes: 60 }),
                  );
                  e.dataTransfer.setData("text/plain", String(t.id));
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragEnd={() => {
                  setDraggedTaskId(null);
                  setDragOverSlotKey(null);
                  setDragOverDayStrip(null);
                }}
                onClick={() => onEditTask(t)}
                className="p-2.5 rounded-xl border bg-card text-xs flex items-center justify-between cursor-grab active:cursor-grabbing hover:border-primary/50 transition-colors select-none"
              >
                <div className="flex items-center gap-1.5 truncate">
                  <GripVertical className="w-3 h-3 text-muted-foreground/50 shrink-0" />
                  <span className="font-semibold truncate">{t.text}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mini Faixa de Dias (Navegação Rápida e Área de Drop para Outros Dias) */}
      <div className="flex items-center gap-1.5 p-1.5 bg-muted/40 rounded-2xl border border-border/70 overflow-x-auto scrollbar-none">
        <span className="text-[11px] font-semibold text-muted-foreground px-2 flex items-center gap-1 shrink-0 select-none">
          <CalendarIcon className="w-3.5 h-3.5 text-primary" />
          <span className="hidden sm:inline">Dias:</span>
        </span>
        <div className="flex items-center gap-1.5 flex-1 min-w-max">
          {dayStrip.map((day) => {
            const isCurrent = isSameDay(day, currentDate);
            const isDayToday = isToday(day);
            const isDragOver = dragOverDayStrip === day.toISOString();

            return (
              <button
                key={day.toISOString()}
                type="button"
                onClick={() => onSelectDate?.(day)}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setDragOverDayStrip(day.toISOString());
                }}
                onDragLeave={() => setDragOverDayStrip(null)}
                onDrop={(e) => handleDropOnDay(day, e)}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all cursor-pointer border select-none",
                  isCurrent
                    ? "bg-primary text-primary-foreground font-bold shadow-xs border-primary"
                    : "bg-background/80 hover:bg-background border-border/60 text-foreground/80 hover:text-foreground",
                  isDragOver &&
                    "ring-2 ring-primary border-primary bg-primary/20 scale-105 shadow-md",
                )}
                title={`Clique para ver ou arraste aqui para mover para ${format(day, "dd/MM")}`}
              >
                <span className="capitalize">
                  {format(day, "EEE", { locale: ptBR })}
                </span>
                <span
                  className={cn(
                    "font-bold",
                    isCurrent ? "text-primary-foreground" : "text-foreground",
                  )}
                >
                  {format(day, "d")}
                </span>
                {isDayToday && !isCurrent && (
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Grid Principal de 24 Horas com Divisão de 15 Minutos */}
      <div
        ref={scrollContainerRef}
        className="relative max-h-[78vh] overflow-y-auto overflow-x-hidden border border-border/80 rounded-2xl bg-card/60 shadow-xs scrollbar-thin select-none"
      >
        <div
          className="relative min-w-[700px] w-full"
          style={{ height: 24 * HOUR_HEIGHT }}
        >
          {/* Linhas de Horário (Background Grid) */}
          {HOURS.map((hour) => (
            <div
              key={hour}
              className="absolute left-0 right-0 flex border-t border-border/70"
              style={{
                top: hour * HOUR_HEIGHT,
                height: HOUR_HEIGHT,
              }}
            >
              {/* Coluna da Esquerda: Rótulo da Hora */}
              <div className="w-16 shrink-0 pr-2 pt-1 text-right text-xs font-mono font-bold text-muted-foreground border-r border-border/70 select-none">
                {String(hour).padStart(2, "0")}:00
              </div>

              {/* Coluna da Direita: 4 slots de 15 minutos clicáveis e com suporte a Drop */}
              <div className="flex-1 flex flex-col relative">
                {QUARTERS.map((minute) => {
                  const slotDate = new Date(currentDate);
                  slotDate.setHours(hour, minute, 0, 0);
                  const isHourStart = minute === 0;
                  const slotKey = `${hour}:${minute}`;
                  const isDragOver = dragOverSlotKey === slotKey;

                  return (
                    <div
                      key={minute}
                      onClick={() => onSelectSlot(slotDate)}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        if (dragOverSlotKey !== slotKey)
                          setDragOverSlotKey(slotKey);
                      }}
                      onDragLeave={(e) => {
                        if (e.currentTarget.contains(e.relatedTarget as Node))
                          return;
                        if (dragOverSlotKey === slotKey)
                          setDragOverSlotKey(null);
                      }}
                      onDrop={(e) => handleDropTaskOnSlot(slotDate, e)}
                      className={cn(
                        "group/slot relative flex-1 border-b border-dashed border-border/30 hover:bg-primary/[0.04] transition-colors cursor-pointer flex items-center px-3",
                        isHourStart && "border-b-border/40",
                        isDragOver &&
                          "bg-primary/20 border-primary border-dashed font-bold z-20",
                      )}
                      title={`Clique para agendar ou solte atividade às ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`}
                    >
                      {/* Sub-label de 15 min no hover ou no arrasto */}
                      <span
                        className={cn(
                          "transition-opacity text-[10px] font-mono text-primary flex items-center gap-1 font-semibold",
                          isDragOver
                            ? "opacity-100 font-bold scale-105"
                            : "opacity-0 group-hover/slot:opacity-100",
                        )}
                      >
                        <Plus className="w-3 h-3" />
                        {isDragOver
                          ? `Soltar às ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
                          : `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Indicador de Linha Vermelha "AGORA" em tempo real */}
          {isToday(currentDate) && (
            <div
              className="absolute left-16 right-0 z-30 pointer-events-none flex items-center"
              style={{ top: currentLineTop }}
            >
              <div className="w-2.5 h-2.5 rounded-full bg-red-500 shadow-sm -ml-1.5" />
              <div className="flex-1 h-0.5 bg-red-500/80" />
            </div>
          )}

          {/* Camada de Eventos / Atividades Posicionadas Proporcionalmente */}
          <div className="absolute top-0 bottom-0 left-16 right-3 pointer-events-none">
            {timedEvents.map((ev) => {
              const {
                task,
                top,
                height,
                column,
                totalColumns,
                durationMinutes,
              } = ev;
              const typeConfig = getActivityTypeConfig(task.type);
              const Icon = typeConfig.icon;
              const isDone = !!task.done_date;
              const isBeingResized = resizingTaskId === task.id;

              // Cálculo de posicionamento horizontal em caso de sobreposição
              const colWidthPct = 100 / totalColumns;
              const leftPct = column * colWidthPct;

              const startTimeStr = format(
                parseISO(task.data_inicio || task.due_date!),
                "HH:mm",
              );
              const endParsed = new Date(
                parseISO(task.data_inicio || task.due_date!).getTime() +
                  durationMinutes * 60 * 1000,
              );
              const endTimeStr = format(endParsed, "HH:mm");

              // Cores temáticas por tipo
              const typeClasses =
                task.type === "reuniao"
                  ? "bg-purple-500/15 border-purple-500/60 text-purple-950 dark:text-purple-100 border-l-purple-500"
                  : task.type === "chamada"
                    ? "bg-blue-500/15 border-blue-500/60 text-blue-950 dark:text-blue-100 border-l-blue-500"
                    : task.type === "visita"
                      ? "bg-emerald-500/15 border-emerald-500/60 text-emerald-950 dark:text-emerald-100 border-l-emerald-500"
                      : task.type === "proposta"
                        ? "bg-amber-500/15 border-amber-500/60 text-amber-950 dark:text-amber-100 border-l-amber-500"
                        : "bg-zinc-500/15 border-zinc-500/60 text-zinc-950 dark:text-zinc-100 border-l-zinc-500";

              return (
                <div
                  key={task.id}
                  draggable={!resizingTaskId}
                  onDragStart={(e) => {
                    setDraggedTaskId(task.id);
                    e.dataTransfer.setData(
                      "application/json",
                      JSON.stringify({ taskId: task.id, durationMinutes }),
                    );
                    e.dataTransfer.setData("text/plain", String(task.id));
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragEnd={() => {
                    setDraggedTaskId(null);
                    setDragOverSlotKey(null);
                    setDragOverDayStrip(null);
                  }}
                  onClick={(e) => {
                    if (
                      isJustResizedRef.current ||
                      resizingTaskId !== null ||
                      draggedTaskId !== null
                    ) {
                      e.stopPropagation();
                      return;
                    }
                    onEditTask(task);
                  }}
                  className={cn(
                    "absolute rounded-xl border border-l-4 p-2 shadow-xs transition-shadow cursor-grab active:cursor-grabbing select-none flex flex-col justify-between overflow-hidden group/card hover:shadow-md hover:z-40",
                    typeClasses,
                    isDone && "opacity-50 line-through grayscale-[30%]",
                    isBeingResized &&
                      "ring-2 ring-primary z-50 shadow-xl opacity-95",
                    draggedTaskId === task.id &&
                      "opacity-30 scale-95 ring-2 ring-primary cursor-grabbing",
                    draggedTaskId !== null && draggedTaskId !== task.id
                      ? "pointer-events-none"
                      : "pointer-events-auto",
                  )}
                  style={{
                    top: `${top + 1}px`,
                    height: `${Math.max(22, height - 2)}px`,
                    left: `calc(${leftPct}% + 4px)`,
                    width: `calc(${colWidthPct}% - 8px)`,
                  }}
                  title={`${task.text} (${startTimeStr} - ${endTimeStr})`}
                >
                  {/* Topo do Evento: Ícone + Horário + Título */}
                  <div className="flex items-start justify-between gap-1.5 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                      <Icon className="w-3.5 h-3.5 shrink-0 opacity-90" />
                      {task.data_fim ? (
                        <>
                          <span className="font-mono text-[11px] font-bold shrink-0">
                            {startTimeStr} – {endTimeStr}
                          </span>
                          <span className="text-[10px] opacity-75 font-semibold">
                            (
                            {durationMinutes >= 60
                              ? `${Math.floor(durationMinutes / 60)}h${durationMinutes % 60 ? `${durationMinutes % 60}m` : ""}`
                              : `${durationMinutes}m`}
                            )
                          </span>
                        </>
                      ) : (
                        <span className="font-mono text-[11px] font-bold shrink-0">
                          {startTimeStr}
                        </span>
                      )}
                    </div>

                    {/* Botões de Ação no Canto */}
                    <div
                      className="flex items-center gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity shrink-0 bg-background/80 rounded-md p-0.5 backdrop-blur-xs"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {task.link_chamada && (
                        <button
                          type="button"
                          onClick={() =>
                            window.open(task.link_chamada!, "_blank")
                          }
                          className="p-1 hover:text-primary rounded text-muted-foreground hover:bg-muted"
                          title="Abrir chamada"
                        >
                          <Video className="w-3 h-3" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => toggleTaskDone(e, task)}
                        className="p-1 hover:text-emerald-600 rounded text-muted-foreground hover:bg-muted"
                        title={isDone ? "Reabrir" : "Concluir"}
                      >
                        <Check className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onEditTask(task)}
                        className="p-1 hover:text-foreground rounded text-muted-foreground hover:bg-muted"
                        title="Editar"
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => deleteTask(e, task)}
                        className="p-1 hover:text-destructive rounded text-muted-foreground hover:bg-muted"
                        title="Excluir"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>

                  {/* Título / Descrição da Atividade */}
                  {height >= 40 && (
                    <div className="mt-0.5 min-w-0">
                      <p className="text-xs font-semibold leading-tight truncate">
                        {task.text}
                      </p>
                    </div>
                  )}

                  {/* Alça Inferior de Redimensionamento Estilo Google Calendar */}
                  <div
                    onMouseDown={(e) =>
                      handleResizeStart(
                        e,
                        task,
                        durationMinutes,
                        ev.startMinutes,
                      )
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    draggable={false}
                    onDragStart={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    className="absolute bottom-0 left-0 right-0 h-3.5 cursor-ns-resize flex items-center justify-center rounded-b-xl hover:bg-black/15 dark:hover:bg-white/15 transition-colors group/handle z-30"
                    title="Clique e arraste para expandir ou retrair o tempo em blocos de 15 minutos"
                  >
                    <div className="w-12 h-1 rounded-full bg-current opacity-40 group-hover/handle:opacity-100 transition-opacity" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
